import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import criterionService from '../services/criterion.service';
import { PatternRedactionElement } from '../types';
import CriterionLevelSelect from './CriterionLevelSelect';

interface Props {
  skillId: number;
}

/** Justifications attendues d'un savoir-faire : appliquées à tout exercice dont une solution mobilise ce SF. */
const SkillCriteriaSection: React.FC<Props> = ({ skillId }) => {
  const [criteria, setCriteria] = useState<PatternRedactionElement[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCriteria(await criterionService.forSkill(skillId));
    } catch {
      toast.error('Erreur lors du chargement des justifications');
    } finally {
      setLoading(false);
    }
  }, [skillId]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = async (id: number, data: Partial<PatternRedactionElement>) => {
    try {
      const updated = await criterionService.update(id, data);
      // ne reprendre que les champs envoyés : un autre champ peut être en cours de saisie
      setCriteria((prev) => prev.map((c) => (c.id === id ? { ...c, ...data, updated_at: updated.updated_at } : c)));
    } catch {
      toast.error('Sauvegarde impossible');
    }
  };

  const edit = (id: number, data: Partial<PatternRedactionElement>) =>
    setCriteria((prev) => prev.map((c) => (c.id === id ? { ...c, ...data } : c)));

  const add = async () => {
    try {
      const created = await criterionService.create({
        owner_skill: skillId, pattern: null, description: '', grader_hint: '', level: 'expected',
        order: criteria.length,
      });
      setCriteria((prev) => [...prev, created]);
    } catch {
      toast.error("Impossible d'ajouter la justification");
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Supprimer cette justification ?')) return;
    try {
      await criterionService.remove(id);
      setCriteria((prev) => prev.filter((c) => c.id !== id));
    } catch {
      toast.error('Suppression impossible');
    }
  };

  const move = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= criteria.length) return;
    const reordered = [...criteria];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(target, 0, moved);
    setCriteria(reordered.map((c, i) => ({ ...c, order: i })));
    try {
      await Promise.all(reordered.map((c, i) => (c.order !== i ? criterionService.update(c.id, { order: i }) : null)));
    } catch {
      toast.error('Réordonnancement impossible');
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
          Justifications attendues ({criteria.length})
        </Typography>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={add}>
          Ajouter
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" mb={1}>
        Ce qui doit apparaître sur la copie pour tout exercice qui mobilise ce savoir-faire. Bloquant : absent ⇒ non validé.
      </Typography>
      {loading ? (
        <Box display="flex" justifyContent="center" p={2}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {criteria.map((c, index) => (
            <Paper key={c.id} variant="outlined" sx={{ p: 1.5 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CriterionLevelSelect value={c.level} onChange={(level) => patch(c.id, { level })} />
                <Box flex={1} />
                <Tooltip title="Monter">
                  <span>
                    <IconButton size="small" onClick={() => move(index, -1)} disabled={index === 0}>
                      <ArrowUpIcon fontSize="inherit" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Descendre">
                  <span>
                    <IconButton size="small" onClick={() => move(index, 1)} disabled={index === criteria.length - 1}>
                      <ArrowDownIcon fontSize="inherit" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Supprimer">
                  <IconButton size="small" onClick={() => remove(c.id)}>
                    <DeleteIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>
              <TextField
                label="Ce qui doit apparaître sur la copie"
                multiline
                minRows={2}
                fullWidth
                size="small"
                value={c.description}
                onChange={(e) => edit(c.id, { description: e.target.value })}
                onBlur={() => patch(c.id, { description: c.description })}
              />
              <TextField
                label="Consigne pour le correcteur"
                multiline
                minRows={1}
                fullWidth
                size="small"
                sx={{ mt: 1 }}
                value={c.grader_hint}
                onChange={(e) => edit(c.id, { grader_hint: e.target.value })}
                onBlur={() => patch(c.id, { grader_hint: c.grader_hint })}
              />
            </Paper>
          ))}
        </Stack>
      )}
    </Paper>
  );
};

export default SkillCriteriaSection;
